import "./ChatbotMenu.scss";

import { Button as AppKitButton } from "@appkit4/react-components";
import { type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AppRoutes } from "../../../../types/enums/appRoutes";
import { AriaLabel } from "../../../../types/enums/ariaLabels";
import { ChatbotMenuLabel } from "../../../../types/enums/labels";

interface ChatbotMenuProps {
  isVisible?: boolean;
}

function ChatbotMenu({ isVisible = true }: ChatbotMenuProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasModalOpenRef = useRef(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isOnAiChat = pathname.startsWith(AppRoutes.aiChat);

  useEffect(() => {
    if (!isModalOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      const firstModalButton = modalRef.current?.querySelector<HTMLButtonElement>("button:not([disabled])");
      firstModalButton?.focus();
    } else if (wasModalOpenRef.current && triggerButtonRef.current && document.contains(triggerButtonRef.current)) {
      triggerButtonRef.current.focus();
    }

    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  if (!isVisible || isOnAiChat) return null;

  const handleOpenAiChat = () => {
    setIsModalOpen(false);
    navigate(AppRoutes.aiChat);
  };

  return (
    <div className="chatbot-menu" ref={menuRef} aria-label={AriaLabel.ChatbotMenu}>
      {isModalOpen ? (
        <section
          id="chatbot-menu-modal"
          role="dialog"
          aria-modal="true"
          className="chatbot-menu__modal"
          aria-label={AriaLabel.ChatbotMenu}
          ref={modalRef}
        >
          <p className="chatbot-menu__modal-title">{ChatbotMenuLabel.MenuTitle}</p>

          <div className="chatbot-menu__modal-actions">
            <AppKitButton
              kind="primary"
              type="button"
              compact
              className="chatbot-menu__modal-action"
              ariaLabel={AriaLabel.OpenAiChat}
              onClick={handleOpenAiChat}
            >
              {ChatbotMenuLabel.OpenAiChat}
            </AppKitButton>

            <AppKitButton
              kind="text"
              type="button"
              className="chatbot-menu__modal-action chatbot-menu__modal-action--text"
              ariaLabel={AriaLabel.CloseChatbotMenu}
              onClick={() => setIsModalOpen(false)}
            >
              {ChatbotMenuLabel.CloseMenu}
            </AppKitButton>
          </div>
        </section>
      ) : null}

      <AppKitButton
        kind="primary"
        type="button"
        compact
        className="chatbot-menu__pill"
        ariaLabel={isModalOpen ? AriaLabel.CloseChatbotMenu : AriaLabel.OpenChatbotMenu}
        aria-controls="chatbot-menu-modal"
        aria-expanded={isModalOpen}
        aria-haspopup="dialog"
        onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
          triggerButtonRef.current = event.currentTarget;
          setIsModalOpen((previousState) => !previousState);
        }}
      >
        <span className="chatbot-menu__pill-content">
          <span className="chatbot-menu__icon-wrap" aria-hidden="true">
            <span className="Appkit4-icon icon-bot-outline chatbot-menu__icon" />
            <span className="chatbot-menu__status-dot" />
          </span>
          <span className="chatbot-menu__copy">
            <span className="chatbot-menu__title">{ChatbotMenuLabel.MenuTitle}</span>
          </span>
        </span>
      </AppKitButton>
    </div>
  );
}

export default ChatbotMenu;
